import { useState, useEffect, useMemo } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQuery, useAction } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Id } from '../../../convex/_generated/dataModel'
import { PlyrPlayer } from '@/components/video/PlyrPlayer'

interface EmbedVideo {
  videoUrl: string | null
  thumbnailUrl: string | null
}

export default function EmbedPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const [video, setVideo] = useState<EmbedVideo | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const embedOptions = useMemo(() => ({
    autoplay: searchParams.get('autoplay') === '1',
    muted: searchParams.get('muted') === '1',
    loop: searchParams.get('loop') === '1',
    controls: searchParams.get('controls') !== '0',
  }), [searchParams])

  const isConvexId = id && id.length > 20
  const isShortId = id && !isConvexId

  const convexVideo = useQuery(
    api.videos.getPublic,
    isConvexId ? { id: id as Id<'videos'> } : 'skip'
  )

  // Try resolving as workflow shortcode
  const workflowData = useQuery(
    api.workflows.getByShortcodePublic,
    isShortId ? { shortcode: id } : 'skip'
  )

  const getLegacyVideo = useAction(api.legacyVideos.getPublicLegacyVideo)

  // Force black background on html/body for the embed
  useEffect(() => {
    const html = document.documentElement
    const body = document.body
    const prevHtmlStyle = html.style.cssText
    const prevBodyStyle = body.style.cssText
    html.style.cssText = 'margin:0;padding:0;height:100%;overflow:hidden;background:#000'
    body.style.cssText = 'margin:0;padding:0;height:100%;overflow:hidden;background:#000'
    return () => {
      html.style.cssText = prevHtmlStyle
      body.style.cssText = prevBodyStyle
    }
  }, [])

  useEffect(() => {
    async function fetchVideo() {
      if (!id) {
        setError(true)
        setLoading(false)
        return
      }

      // Convex video ID (long ID)
      if (isConvexId) {
        if (convexVideo !== undefined) {
          if (convexVideo) {
            setVideo({
              videoUrl: convexVideo.videoUrl ?? null,
              thumbnailUrl: convexVideo.thumbnailUrl ?? null,
            })
          } else {
            setError(true)
          }
          setLoading(false)
        }
        return
      }

      // Short ID: try workflow shortcode first, then legacy video
      if (isShortId) {
        if (workflowData === undefined) return

        if (workflowData && workflowData.finalVideoUrl) {
          setVideo({
            videoUrl: workflowData.finalVideoUrl,
            thumbnailUrl: null,
          })
          setLoading(false)
          return
        }

        // Not a workflow shortcode — try legacy
        try {
          const legacyVideo = await getLegacyVideo({ shortcode: id })
          if (legacyVideo) {
            setVideo({
              videoUrl: legacyVideo.videoUrl,
              thumbnailUrl: legacyVideo.thumbnailUrl,
            })
          } else {
            setError(true)
          }
        } catch {
          setError(true)
        }
        setLoading(false)
      }
    }

    fetchVideo()
  }, [id, isConvexId, isShortId, convexVideo, workflowData, getLegacyVideo])

  if (loading || error || !video?.videoUrl) {
    return null
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        width: '100%',
        height: '100%',
        background: '#000',
      }}
    >
      <PlyrPlayer
        src={video.videoUrl}
        poster={video.thumbnailUrl || undefined}
        autoPlay={embedOptions.autoplay}
        muted={embedOptions.muted}
        loop={embedOptions.loop}
        controls={embedOptions.controls}
      />
      <style>{`
        .plyr-wrapper, .plyr-wrapper .plyr-container, .plyr-wrapper .plyr {
          border-radius: 0 !important;
        }
      `}</style>
    </div>
  )
}
